import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ChevronDown, Sun, Moon, Utensils, ShieldCheck, ArrowRight, Leaf, Droplets, Heart, X } from 'lucide-react';
const LegalModal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => <AnimatePresence data-id="element-0">
    {isOpen && <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-1">
        <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" data-id="element-2" />
        <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="relative bg-warmWhite rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-3">
          <div className="flex items-center justify-between p-6 border-b border-blush" data-id="element-4">
            <h3 className="font-serif text-2xl font-bold text-charcoal" data-id="element-5">
              {title}
            </h3>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-blush-light transition-colors text-charcoal/60 hover:text-charcoal" data-id="element-6">
              <X className="w-5 h-5" data-id="element-7" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-charcoal/80 leading-relaxed space-y-4 text-sm" data-id="element-8">
            {children}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-9">
    {children}
  </motion.div>;
const CTAButton = ({
  className = '',
  externalHref
}: {
  className?: string;
  externalHref?: string;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (externalHref) return;
    e.preventDefault();
    const offerSection = document.getElementById('offer');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  return <motion.a href={externalHref || '#offer'} target={externalHref ? '_blank' : undefined} rel={externalHref ? 'noopener noreferrer' : undefined} onClick={handleClick} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral-hover text-white font-bold text-lg md:text-xl py-4 px-8 rounded-full shadow-lg transition-colors cursor-pointer w-full md:w-auto ${className}`} data-id="element-10">
      ¡QUIERO MI VIENTRE PLANO AHORA!
      <ArrowRight className="w-5 h-5" data-id="element-11" />
    </motion.a>;
};
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-blush pb-4" data-id="element-12">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between text-left py-4 focus:outline-none" data-id="element-13">
        <span className="font-serif text-xl text-charcoal font-semibold pr-4" data-id="element-14">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0 text-gold" data-id="element-15">
          <ChevronDown className="w-6 h-6" data-id="element-16" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-17">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-18">
            <p className="text-charcoal/80 pb-4 leading-relaxed" data-id="element-19">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function App() {
  useEffect(() => {
    // Meta Pixel Code
    const f = window as any;
    if (f.fbq) return;
    const n: any = f.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
    f.fbq('init', '793243770209630');
    f.fbq('track', 'PageView');
    // noscript fallback
    const noscriptImg = document.createElement('img');
    noscriptImg.height = 1;
    noscriptImg.width = 1;
    noscriptImg.style.display = 'none';
    noscriptImg.src = 'https://www.facebook.com/tr?id=793243770209630&ev=PageView&noscript=1';
    document.body.appendChild(noscriptImg);
    return () => {
      script.remove();
      noscriptImg.remove();
    };
  }, []);
  return <div className="font-sans text-charcoal bg-warmWhite min-h-screen selection:bg-mint selection:text-charcoal" data-id="element-20">
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-20 md:pt-20 md:pb-32 overflow-hidden" data-id="element-21">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blush-light/50 to-transparent -z-10" data-id="element-22" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-mint/20 rounded-full blur-3xl -z-10" data-id="element-23" />

        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center" data-id="element-24">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="text-center md:text-left" data-id="element-25">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-gold font-semibold text-sm mb-6 shadow-sm border border-gold/20" data-id="element-26">
              <Leaf className="w-4 h-4" data-id="element-27" />
              <span data-id="element-28">Para mujeres de 40+</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-charcoal" data-id="element-29">
              Dile adiós a la{' '}
              <span className="text-gold italic" data-id="element-30">"Panza Hormonal"</span> en solo
              7 días
            </h1>
            <p className="text-xl md:text-2xl text-charcoal/80 mb-4 font-light" data-id="element-31">
              (sin pasar hambre ni hacer cardio extenuante)
            </p>
            <p className="text-lg mb-8 leading-relaxed" data-id="element-32">
              El protocolo de reequilibrio mineral diseñado específicamente para
              mujeres de 40+ que sienten que "el aire las engorda".
            </p>
            <CTAButton data-id="element-33" />
            <div className="mt-6 flex items-center justify-center md:justify-start gap-2 text-sm text-charcoal/60" data-id="element-34">
              <ShieldCheck className="w-4 h-4 text-mint" data-id="element-35" />
              <span data-id="element-36">Acceso inmediato • Pago seguro</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-37">
            <div className="absolute inset-0 bg-gradient-to-tr from-blush to-mint rounded-3xl transform rotate-3 scale-105 opacity-20 blur-xl -z-10" data-id="element-38" />
            <img src="https://cdn.magicpatterns.com/uploads/gutUYDmoUGHjH5mu6rfhRm/caratura.png" alt="Vientre Plano Post-40" className="w-full h-auto rounded-2xl shadow-2xl relative z-10 object-cover" data-id="element-39" />
          </motion.div>
        </div>
      </section>

      {/* HOOK & PROBLEM */}
      <section className="py-20 bg-blush-light relative" data-id="element-40">
        <div className="max-w-3xl mx-auto px-6" data-id="element-41">
          <FadeIn data-id="element-42">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl relative" data-id="element-43">
              <Heart className="absolute -top-6 -left-6 w-12 h-12 text-coral opacity-20" data-id="element-44" />
              <p className="text-xl md:text-2xl leading-relaxed font-serif italic text-charcoal mb-8" data-id="element-45">
                "Son las 7:30 de la mañana. Te paras frente al espejo, te pones
                tus jeans favoritos y, de repente, el corazón se te desploma. No
                cierran."
              </p>
              <div className="space-y-6 text-lg text-charcoal/80" data-id="element-46">
                <p data-id="element-47">
                  No es que hayas comido mal ayer. Es esa inflamación rebelde
                  que aparece de la nada, que te hace sentir pesada, incómoda y
                  con un abdomen que parece tener vida propia. Si tienes más de
                  40 años, lo que ves en el espejo no es solo grasa; es tu
                  cuerpo pidiendo auxilio hormonal.
                </p>
                <h3 className="font-serif text-2xl font-bold text-charcoal pt-4" data-id="element-48">
                  Después de los 40, las reglas del metabolismo cambian.
                </h3>
                <p data-id="element-49">
                  Tus hormonas entran en una montaña rusa que dispara el
                  cortisol, la hormona del estrés.
                </p>
                <p data-id="element-50">
                  ¿El resultado? Tu cuerpo entra en modo de pánico y empieza a
                  "secuestrar" agua y sodio justo en tu zona abdominal. Por eso,
                  aunque comas ensalada y camines 10,000 pasos, esa hinchazón no
                  se va.{' '}
                  <strong className="text-coral font-semibold" data-id="element-51">
                    Estás luchando contra tu biología, no contra las calorías.
                  </strong>
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20" data-id="element-52">
        <div className="max-w-4xl mx-auto px-6" data-id="element-53">
          <FadeIn data-id="element-54">
            <div className="text-center mb-12" data-id="element-55">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-56">
                Por qué lo que has intentado no funciona
              </h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-57" />
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-58">
            {['Las dietas hipocalóricas aumentan el estrés interno, disparando aún más el cortisol y la retención de líquidos.', "El cardio intenso agota tus glándulas suprarrenales, haciendo que tu vientre se inflame por 'protección'.", 'Los suplementos de magnesio baratos (como el óxido) no se absorben y solo irritan tu sistema digestivo.', 'Beber agua sola, sin el equilibrio mineral adecuado, paradójicamente te hace retener más líquido en los tejidos.'].map((text, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-59">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-blush flex items-start gap-4 h-full" data-id="element-60">
                  <XCircle className="w-6 h-6 text-coral flex-shrink-0 mt-1" data-id="element-61" />
                  <p className="text-charcoal/80" data-id="element-62">{text}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-20 bg-charcoal text-warmWhite relative overflow-hidden" data-id="element-63">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl" data-id="element-64" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-65">
          <FadeIn data-id="element-66">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6 text-gold" data-id="element-67">
              Presentamos el Protocolo de Ciclado Mag-So
            </h2>
            <p className="text-xl md:text-2xl font-light mb-8 text-warmWhite/90" data-id="element-68">
              No es una dieta, es un sistema de 24 horas.
            </p>
            <p className="text-lg leading-relaxed text-warmWhite/80 max-w-3xl mx-auto" data-id="element-69">
              Utiliza el poder del Magnesio nocturno y el Sodio matutino de alta
              calidad para "empujar" el agua fuera de tus células. Este método
              resetea tu señal hormonal, relaja tu sistema nervioso y permite
              que tu cuerpo suelte naturalmente el exceso de volumen abdominal
              mientras duermes.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20" data-id="element-70">
        <div className="max-w-5xl mx-auto px-6" data-id="element-71">
          <FadeIn data-id="element-72">
            <div className="text-center mb-16" data-id="element-73">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-74">
                Lo que obtienes hoy
              </h2>
              <div className="w-24 h-1 bg-mint mx-auto rounded-full" data-id="element-75" />
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-76">
            <FadeIn data-id="element-77">
              <img src="https://cdn.magicpatterns.com/uploads/gutUYDmoUGHjH5mu6rfhRm/caratura.png" alt="Contenido del programa" className="w-full rounded-2xl shadow-xl" data-id="element-78" />
            </FadeIn>
            <div className="space-y-6" data-id="element-79">
              {[{
              title: 'Guía Maestra Vientre Plano Post-40',
              desc: 'El paso a paso detallado para resetear tu metabolismo hormonal.'
            }, {
              title: 'Protocolo de Ciclado Mag-So',
              desc: 'Instrucciones exactas sobre qué minerales tomar y en qué momento preciso del día.'
            }, {
              title: 'Menú Anti-Inflamatorio de 7 Días',
              desc: 'Comidas deliciosas y saciantes que desactivan la inflamación visceral.'
            }, {
              title: 'Rutina de 5 Minutos de Rescate',
              desc: 'Movimientos linfáticos simples para drenar el abdomen al despertar.'
            }, {
              title: "Lista de Compras 'Cero Hinchazón'",
              desc: 'Para que sepas exactamente qué meter en tu carrito y qué dejar en el estante.'
            }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-80">
                  <div className="flex gap-4" data-id="element-81">
                    <div className="mt-1 bg-mint/30 p-1 rounded-full h-fit" data-id="element-82">
                      <CheckCircle2 className="w-5 h-5 text-charcoal" data-id="element-83" />
                    </div>
                    <div data-id="element-84">
                      <h4 className="font-bold text-lg text-charcoal" data-id="element-85">
                        {item.title}
                      </h4>
                      <p className="text-charcoal/70" data-id="element-86">{item.desc}</p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-blush-light/50" data-id="element-87">
        <div className="max-w-5xl mx-auto px-6" data-id="element-88">
          <FadeIn data-id="element-89">
            <div className="text-center mb-16" data-id="element-90">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-91">
                Cómo funciona
              </h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-92" />
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-93">
            {[{
            icon: Sun,
            title: '1. Activación Matutina',
            desc: "Bebes la bebida 'Adrenal' para calmar el cortisol desde la primera hora."
          }, {
            icon: Utensils,
            title: '2. Nutrición Estratégica',
            desc: 'Sigues el menú de 7 días diseñado para no retener líquidos.'
          }, {
            icon: Moon,
            title: '3. Reparación Nocturna',
            desc: 'Tomas la dosis exacta de magnesio para desinflamar mientras descansas profundamente.'
          }].map((step, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-94">
                <div className="bg-white p-8 rounded-3xl shadow-lg text-center h-full border-t-4 border-gold" data-id="element-95">
                  <div className="w-16 h-16 mx-auto bg-blush rounded-full flex items-center justify-center mb-6" data-id="element-96">
                    <step.icon className="w-8 h-8 text-charcoal" data-id="element-97" />
                  </div>
                  <h3 className="font-serif text-xl font-bold mb-4" data-id="element-98">
                    {step.title}
                  </h3>
                  <p className="text-charcoal/70" data-id="element-99">{step.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR / NOT FOR */}
      <section className="py-20" data-id="element-100">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12" data-id="element-101">
          <FadeIn data-id="element-102">
            <div className="bg-mint/10 p-8 rounded-3xl h-full border border-mint/30" data-id="element-103">
              <h3 className="font-serif text-2xl font-bold text-charcoal mb-6 flex items-center gap-2" data-id="element-104">
                <CheckCircle2 className="text-mint w-6 h-6" data-id="element-105" />
                Para quién es esto
              </h3>
              <ul className="space-y-4" data-id="element-106">
                {['Mujeres de 40 a 60 años que notan un cambio súbito en su zona abdominal.', 'Mujeres que sufren de inflamación que empeora durante el día (bloating).', 'Mujeres con altos niveles de estrés que no logran bajar de peso con métodos tradicionales.', 'Quienes buscan una solución natural, rápida y basada en la biología femenina.'].map((text, i) => <li key={i} className="flex gap-3 text-charcoal/80" data-id="element-107">
                    <span className="text-mint mt-1" data-id="element-108">•</span>
                    <span data-id="element-109">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-110">
            <div className="bg-blush/10 p-8 rounded-3xl h-full border border-blush/30" data-id="element-111">
              <h3 className="font-serif text-2xl font-bold text-charcoal mb-6 flex items-center gap-2" data-id="element-112">
                <XCircle className="text-coral w-6 h-6" data-id="element-113" />
                Para quién NO es
              </h3>
              <ul className="space-y-4" data-id="element-114">
                {["Personas que buscan 'pastillas mágicas' para adelgazar sin cambiar hábitos básicos.", 'Atletas de alto rendimiento que ya tienen un control mineral estricto.', 'Hombres (este protocolo está diseñado para la cascada hormonal femenina).'].map((text, i) => <li key={i} className="flex gap-3 text-charcoal/80" data-id="element-115">
                    <span className="text-coral mt-1" data-id="element-116">•</span>
                    <span data-id="element-117">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF */}
      <section className="py-16 bg-gold/10" data-id="element-118">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-119">
          <FadeIn data-id="element-120">
            <Droplets className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-121" />
            <p className="font-serif text-2xl md:text-3xl text-charcoal leading-relaxed italic" data-id="element-122">
              "En solo 7 días, notarás cómo la marca del elástico de tu ropa
              desaparece, tus digestiones se vuelven ligeras y recuperas esa
              sensación de agilidad que creías perdida. No es magia, es
              mineralogía aplicada."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20" data-id="element-123">
        <div className="max-w-3xl mx-auto px-6" data-id="element-124">
          <FadeIn data-id="element-125">
            <div className="text-center mb-12" data-id="element-126">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-127">
                Preguntas Frecuentes
              </h2>
              <div className="w-24 h-1 bg-mint mx-auto rounded-full" data-id="element-128" />
            </div>
          </FadeIn>

          <div className="space-y-2" data-id="element-129">
            <FadeIn delay={0.1} data-id="element-130">
              <FAQItem question="¿Tengo que comprar suplementos caros?" answer="No, solo necesitas magnesio de buena calidad y sal marina auténtica, cosas que encuentras en cualquier tienda de salud." data-id="element-131" />
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-132">
              <FAQItem question="¿Pasaré hambre?" answer="Absolutamente no. El protocolo incluye grasas saludables y proteínas que te mantienen saciada." data-id="element-133" />
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-134">
              <FAQItem question="¿Es seguro si tengo presión alta?" answer="El protocolo enfatiza el equilibrio, pero siempre recomendamos consultar con tu médico antes de ajustar tu consumo de minerales." data-id="element-135" />
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-136">
              <FAQItem question="¿Cuánto tiempo tardaré en ver resultados?" answer="La mayoría de las mujeres reportan una reducción visible de la hinchazón en las primeras 48 a 72 horas." data-id="element-137" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* OFFER & CTA */}
      <section id="offer" className="py-20 bg-charcoal text-warmWhite relative overflow-hidden" data-id="element-138">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/5 rounded-full blur-3xl pointer-events-none" data-id="element-139" />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-140">
          <FadeIn data-id="element-141">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6 text-gold" data-id="element-142">
              Recupera tu confianza
            </h2>
            <p className="text-xl mb-12 text-warmWhite/80 max-w-2xl mx-auto" data-id="element-143">
              Y vuelve a usar la ropa que amas. Obtén el sistema completo
              "Vientre Plano Post-40" hoy mismo por un pago único. No es una
              suscripción, es tu acceso de por vida a la libertad abdominal.
            </p>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 md:p-12 rounded-3xl max-w-2xl mx-auto shadow-2xl" data-id="element-144">
              <div className="text-mint font-semibold uppercase tracking-wider text-sm mb-2" data-id="element-145">
                Precio Especial de Lanzamiento
              </div>
              <div className="flex justify-center items-center gap-3 mb-2" data-id="element-146">
                <span className="text-2xl text-warmWhite/40 line-through" data-id="element-147">
                  $27.00 USD
                </span>
              </div>
              <div className="flex justify-center items-start gap-1 mb-2" data-id="element-148">
                <span className="text-3xl font-bold mt-2 text-gold" data-id="element-149">$</span>
                <span className="text-7xl font-bold text-white" data-id="element-150">6</span>
                <span className="text-xl font-bold mt-8 text-gold" data-id="element-151">
                  .39 USD
                </span>
              </div>
              <div className="inline-block bg-coral/20 text-coral font-bold text-sm px-4 py-1 rounded-full mb-8" data-id="element-152">
                ¡Ahorra 76% hoy!
              </div>

              <CTAButton className="w-full md:w-auto px-12 py-5 text-xl" externalHref="https://conmelita.com//cart/62682592313715:1" data-id="element-153" />

              <div className="mt-6 flex flex-col items-center gap-3 text-sm text-warmWhite/60" data-id="element-154">
                <div className="flex items-center gap-2" data-id="element-155">
                  <ShieldCheck className="w-5 h-5 text-mint" data-id="element-156" />
                  <span data-id="element-157">Compra 100% segura y encriptada</span>
                </div>
                <p data-id="element-158">Acceso inmediato al PDF tras el pago.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <Footer data-id="element-159" />
    </div>;
}
function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="bg-charcoal border-t border-white/10 py-8 text-center text-warmWhite/40 text-sm" data-id="element-160">
        <p data-id="element-161">
          © {new Date().getFullYear()} Vientre Plano Post-40. Todos los
          derechos reservados.
        </p>
        <p className="mt-2 max-w-2xl mx-auto px-6 text-xs" data-id="element-162">
          Este producto no sustituye el consejo médico profesional. Los
          resultados pueden variar.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4" data-id="element-163">
          <button onClick={() => setPrivacyOpen(true)} className="underline underline-offset-2 hover:text-warmWhite transition-colors" data-id="element-164">
            Política de Privacidad
          </button>
          <span className="text-warmWhite/20" data-id="element-165">|</span>
          <button onClick={() => setTermsOpen(true)} className="underline underline-offset-2 hover:text-warmWhite transition-colors" data-id="element-166">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-167">
        <p className="font-semibold text-charcoal" data-id="element-168">
          Última actualización: Abril 2026
        </p>
        <p data-id="element-169">
          En Vientre Plano Post-40 nos comprometemos a proteger tu privacidad.
          Esta política describe cómo recopilamos, usamos y protegemos tu
          información personal.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-170">
          1. Información que recopilamos
        </h4>
        <p data-id="element-171">
          Recopilamos únicamente la información necesaria para procesar tu
          compra: nombre, dirección de correo electrónico y datos de pago. No
          almacenamos datos de tarjetas de crédito; estos son procesados de
          forma segura por nuestro proveedor de pagos.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-172">
          2. Uso de la información
        </h4>
        <p data-id="element-173">
          Tu información se utiliza exclusivamente para: procesar tu pedido,
          enviarte el producto digital adquirido y comunicarnos contigo respecto
          a tu compra. No vendemos, alquilamos ni compartimos tu información
          personal con terceros con fines de marketing.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-174">3. Protección de datos</h4>
        <p data-id="element-175">
          Utilizamos cifrado SSL y medidas de seguridad estándar de la industria
          para proteger tu información personal durante la transmisión y el
          almacenamiento.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-176">4. Cookies</h4>
        <p data-id="element-177">
          Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
          navegación y analizar el tráfico del sitio. Puedes configurar tu
          navegador para rechazar cookies si lo prefieres.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-178">5. Tus derechos</h4>
        <p data-id="element-179">
          Tienes derecho a acceder, rectificar o eliminar tu información
          personal en cualquier momento. Para ejercer estos derechos, puedes
          contactarnos a través de los canales disponibles en nuestra plataforma
          de venta.
        </p>
      </LegalModal>

      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-180">
        <p className="font-semibold text-charcoal" data-id="element-181">
          Última actualización: Abril 2026
        </p>
        <p data-id="element-182">
          Al adquirir el programa "Vientre Plano Post-40", aceptas los
          siguientes términos y condiciones. Por favor, léelos cuidadosamente
          antes de realizar tu compra.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-183">1. Producto digital</h4>
        <p data-id="element-184">
          Este es un producto digital en formato PDF. Tras completar el pago,
          recibirás acceso inmediato al material. Debido a la naturaleza digital
          del producto, todas las ventas son finales.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-185">2. Uso personal</h4>
        <p data-id="element-186">
          El contenido adquirido es para uso personal y exclusivo del comprador.
          Queda prohibida la reproducción, distribución, modificación o reventa
          del material sin autorización expresa por escrito.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-187">
          3. Descargo de responsabilidad médica
        </h4>
        <p data-id="element-188">
          Este programa es de carácter informativo y educativo. No sustituye el
          consejo, diagnóstico o tratamiento médico profesional. Siempre
          consulta con tu médico antes de iniciar cualquier programa nutricional
          o de suplementación, especialmente si tienes condiciones de salud
          preexistentes.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-189">4. Resultados</h4>
        <p data-id="element-190">
          Los resultados pueden variar de persona a persona. Los testimonios y
          ejemplos mencionados no garantizan que obtendrás los mismos
          resultados, ya que estos dependen de múltiples factores individuales.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-191">
          5. Propiedad intelectual
        </h4>
        <p data-id="element-192">
          Todo el contenido del programa, incluyendo textos, diseños, gráficos y
          metodologías, está protegido por derechos de autor y es propiedad
          exclusiva de Vientre Plano Post-40.
        </p>
        <h4 className="font-bold text-charcoal pt-2" data-id="element-193">6. Modificaciones</h4>
        <p data-id="element-194">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones entrarán en vigor desde su publicación en
          el sitio web.
        </p>
      </LegalModal>
    </>;
}